import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Container,
  Flashbar,
  Grid,
  Header,
  Select,
  SpaceBetween,
} from '@amzn/awsui-components-console';
import {
  GroupSummary,
  ListGroupsCommand,
  ListGroupsCommandOutput,
  ListVenuesCommand,
  ListVenuesCommandOutput,
  VenueInfoItem,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import { ApiErrorProvider, useApiErrors } from '../components/ApiErrorContext';
import { ApiErrorNotifications } from '../components/ApiErrorNotifications';
import { callApi } from '../client';
import { venueDisplayName as venueDisplayNameFor } from '../utils/venueDisplay';
import { GroupListTable } from './GroupListTable';
import { paginateAll, PAGE_SIZE } from '../utils/pagination';
import {
  useConferenceVenueScope,
  ALL_CONFERENCES,
  ALL_VENUES,
} from '../hooks/useConferenceVenueScope';
import { useLatestRequest } from '../hooks/useLatestRequest';

function GroupListContent() {
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [venues, setVenues] = useState<VenueInfoItem[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  // A failed venue load is non-fatal (the list still works via the All accessible default), but the
  // scope dropdowns go silently empty — surface a lightweight inline notice so the coordinator knows
  // why (finding on CR-301380769).
  const [venuesError, setVenuesError] = useState(false);

  const location = useLocation<{ banner?: string }>();
  const { isAuthenticated, token } = useAuthState();
  const { pushError } = useApiErrors();

  // Shared conference/venue scope state + derived Select options (D6).
  const scope = useConferenceVenueScope(venues);
  // Latest-request-wins guard: a slow broad walk must not overwrite a newer narrow scope's result
  // (finding on CR-301380769).
  const groupsRequest = useLatestRequest();

  // Show banner from navigation state (e.g., auto-delete redirect)
  useEffect(() => {
    if (location.state?.banner) {
      setBanner(location.state.banner);
    }
  }, [location.state]);

  useEffect(() => {
    if (isAuthenticated && token) {
      void fetchVenues();
    }
  }, [isAuthenticated, token]);

  // Re-fetch the scoped group list whenever the selected scope changes. An ALL_CONFERENCES scope
  // sends no conference so the backend returns every group the caller can read (D6).
  useEffect(() => {
    if (isAuthenticated && token) {
      void fetchGroups(scope.scopedConference, scope.scopedVenue);
    }
  }, [isAuthenticated, token, scope.conference, scope.venue]);

  async function fetchVenues() {
    if (!token) return;
    setVenuesLoading(true);
    try {
      const response: ListVenuesCommandOutput = await callApi(
        new ListVenuesCommand({}),
        token
      );
      if (response.$metadata.httpStatusCode === 200) {
        setVenues(response.venues ?? []);
        setVenuesError(false);
      } else {
        setVenuesError(true);
      }
    } catch (err: any) {
      // Non-fatal: without venues the scope selects stay empty (defaulting to All accessible). Flag it
      // inline rather than only console.error'ing so the empty dropdowns are explained (CR-301380769).
      console.error('Failed to load venues:', err);
      setVenuesError(true);
    } finally {
      setVenuesLoading(false);
    }
  }

  async function fetchGroups(conf?: string, ven?: string) {
    if (!token) {
      return;
    }

    try {
      await groupsRequest.run(
        setLoading,
        () =>
          // Page through every result rather than the first ≤1000 (tcx-core-platforms-903 §8 F7).
          paginateAll<GroupSummary>((nextToken) =>
            callApi(
              new ListGroupsCommand({
                conference: conf,
                venue: ven,
                pageSize: PAGE_SIZE,
                nextToken,
              }),
              token
            ).then((response: ListGroupsCommandOutput) => ({
              items: response.groups ?? [],
              nextToken: response.nextToken,
              statusCode: response.$metadata.httpStatusCode ?? 0,
            }))
          ),
        ({ items, ok, truncated }) => {
          // Commit only on a fully-successful, non-truncated walk; a truncated result (more pages than
          // the cap allows) is surfaced as an error rather than a silent partial list (CR-301380769
          // rev2). The superseded-fetch drop and loading-flag reset are owned by run() (rev5).
          if (ok && !truncated) {
            setGroups(items);
          } else if (ok) {
            pushError(
              'Failed to load groups',
              'Too many pages of groups to load; please narrow the conference/venue filter.'
            );
          } else {
            pushError(
              'Failed to load groups',
              'Unexpected response from server'
            );
          }
        }
      );
    } catch (err: any) {
      pushError('Failed to load groups', err?.message || String(err));
    }
  }

  const refresh = () => {
    void fetchGroups(scope.scopedConference, scope.scopedVenue);
  };

  // Display-only venue name: coordinator canonical override from the venue list, else the raw venue
  // (sorting/filtering/identity stay on the raw venue). tcx-core-platforms-903 D2.
  const venueDisplayName = (conf?: string, ven?: string): string =>
    venueDisplayNameFor(venues, conf, ven);

  return (
    <SpaceBetween size='l'>
      {banner && (
        <Flashbar
          items={[
            {
              type: 'success',
              content: banner,
              dismissible: true,
              onDismiss: () => setBanner(null),
              id: 'auto-delete-banner',
            },
          ]}
        />
      )}
      <Container
        header={
          <Header
            variant='h2'
            description='Scope the group list to a conference (and optionally one venue within it). Defaults to every conference you can access.'
          >
            Filter
          </Header>
        }
      >
        <SpaceBetween size='s'>
          {venuesError && (
            <Alert
              type='warning'
              dismissible
              onDismiss={() => setVenuesError(false)}
            >
              Could not load conference/venue filters — showing all accessible
              results.
            </Alert>
          )}
          <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
            <Box>
              <Box variant='awsui-key-label'>Conference</Box>
              <Select
                selectedOption={scope.selectedConferenceOption}
                onChange={({ detail }) =>
                  scope.selectConference(detail.selectedOption.value)
                }
                options={scope.conferenceOptions}
                placeholder='All accessible'
                loadingText='Loading conferences...'
                statusType={venuesLoading ? 'loading' : 'finished'}
                empty='No conferences available'
                data-test-id='conference-scope-select'
              />
            </Box>
            <Box>
              <Box variant='awsui-key-label'>Venue</Box>
              <Select
                selectedOption={scope.selectedVenueOption}
                onChange={({ detail }) =>
                  scope.setVenue(detail.selectedOption.value ?? ALL_VENUES)
                }
                options={scope.venueOptions}
                disabled={scope.conference === ALL_CONFERENCES}
                placeholder='All venues'
                data-test-id='venue-scope-select'
              />
            </Box>
          </Grid>
        </SpaceBetween>
      </Container>
      <GroupListTable
        data={groups}
        loading={loading}
        onRefresh={refresh}
        venueDisplayName={venueDisplayName}
      />
    </SpaceBetween>
  );
}

export function GroupList() {
  return (
    <ApiErrorProvider>
      <div>
        <ApiErrorNotifications />
        <GroupListContent />
      </div>
    </ApiErrorProvider>
  );
}

export default GroupList;
